/**
 * SubtaskList - Nested subtask display with add/complete/delete.
 *
 * Renders subtasks with checkboxes, allows adding new ones (up to 20),
 * shows count indicator "X/20 subtasks", and each subtask has a delete button.
 *
 * @see Requirements 2.3, 2.5
 */

import { useCallback, useState, useRef } from 'react';
import type { Subtask } from '@/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/** Maximum number of subtasks allowed per task. */
const MAX_SUBTASKS = 20;

export interface SubtaskListProps {
  /** Array of subtasks to display. */
  subtasks: Subtask[];
  /** Called when subtasks are modified (add, complete, delete). */
  onChange: (subtasks: Subtask[]) => void;
  /** Whether the parent task is completed (disables editing). */
  disabled?: boolean;
}

export function SubtaskList({ subtasks, onChange, disabled = false }: SubtaskListProps) {
  const [newTitle, setNewTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const atCapacity = subtasks.length >= MAX_SUBTASKS;

  const handleAddSubtask = useCallback(() => {
    const trimmed = newTitle.trim();
    if (!trimmed) {
      setError('Subtask title is required.');
      return;
    }
    if (trimmed.length > 500) {
      setError('Subtask title must be 500 characters or fewer.');
      return;
    }
    if (atCapacity) {
      setError('Maximum of 20 subtasks reached.');
      return;
    }

    const newSubtask: Subtask = {
      subtaskId: crypto.randomUUID(),
      title: trimmed,
      isCompleted: false,
      completedAt: null,
      sortOrder: subtasks.length,
    };

    onChange([...subtasks, newSubtask]);
    setNewTitle('');
    setError(null);
    inputRef.current?.focus();
  }, [newTitle, subtasks, onChange, atCapacity]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddSubtask();
      }
    },
    [handleAddSubtask]
  );

  const handleToggleComplete = useCallback(
    (subtaskId: string) => {
      const now = new Date().toISOString();
      const updated = subtasks.map((st) =>
        st.subtaskId === subtaskId
          ? {
              ...st,
              isCompleted: !st.isCompleted,
              completedAt: !st.isCompleted ? now : null,
            }
          : st
      );
      onChange(updated);
    },
    [subtasks, onChange]
  );

  const handleDelete = useCallback(
    (subtaskId: string) => {
      const updated = subtasks.filter((st) => st.subtaskId !== subtaskId);
      onChange(updated);
    },
    [subtasks, onChange]
  );

  const completedCount = subtasks.filter((st) => st.isCompleted).length;

  return (
    <div className="flex flex-col gap-3">
      {/* Header with count */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-text-secondary">Subtasks</h4>
        <span
          className="text-xs text-text-secondary"
          aria-label={`${subtasks.length} of ${MAX_SUBTASKS} subtasks used, ${completedCount} completed`}
        >
          {subtasks.length}/{MAX_SUBTASKS} subtasks
        </span>
      </div>

      {/* Subtask list */}
      {subtasks.length > 0 && (
        <ul className="flex flex-col gap-1" role="list" aria-label="Subtasks">
          {subtasks.map((subtask) => (
            <li
              key={subtask.subtaskId}
              className="flex items-center gap-2 group px-2 py-1.5 rounded-sm hover:bg-hover-bg transition-colors duration-fast"
            >
              {/* Checkbox */}
              <button
                type="button"
                role="checkbox"
                aria-checked={subtask.isCompleted}
                aria-label={`Mark "${subtask.title}" as ${subtask.isCompleted ? 'incomplete' : 'complete'}`}
                disabled={disabled}
                onClick={() => handleToggleComplete(subtask.subtaskId)}
                className={[
                  'flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center',
                  'transition-all duration-fast ease-default',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  subtask.isCompleted
                    ? 'bg-primary border-primary'
                    : 'border-border hover:border-primary',
                ].join(' ')}
              >
                {subtask.isCompleted && (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 6L5 9L10 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>

              {/* Title */}
              <span
                className={[
                  'flex-1 text-sm truncate',
                  subtask.isCompleted
                    ? 'line-through text-text-secondary opacity-60'
                    : 'text-text-primary',
                ].join(' ')}
              >
                {subtask.title}
              </span>

              {/* Delete button */}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Delete subtask "${subtask.title}"`}
                  onClick={() => handleDelete(subtask.subtaskId)}
                  className={[
                    'flex-shrink-0 w-6 h-6 flex items-center justify-center rounded',
                    'text-text-secondary opacity-0 group-hover:opacity-100',
                    'hover:bg-primary/10 hover:text-primary-dark',
                    'transition-all duration-fast',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:opacity-100',
                  ].join(' ')}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 3L11 11M11 3L3 11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add subtask input */}
      {!disabled && (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              ref={inputRef}
              size="sm"
              placeholder={atCapacity ? 'Maximum subtasks reached' : 'Add a subtask...'}
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={handleKeyDown}
              disabled={atCapacity}
              error={error ?? undefined}
              aria-label="New subtask title"
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleAddSubtask}
            disabled={atCapacity || !newTitle.trim()}
            aria-label="Add subtask"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M8 3V13M3 8H13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Button>
        </div>
      )}

      {/* Capacity warning */}
      {atCapacity && !disabled && (
        <p className="text-xs text-text-secondary" role="status">
          Maximum of 20 subtasks reached. Delete existing subtasks to add more.
        </p>
      )}
    </div>
  );
}
